import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { Button } from "@/components/ui/button";
import { Camera, X } from "lucide-react";

/** Extrait le jeton de carte depuis le contenu d'un QR (URL /carte/{token}, URL ancienne, ou jeton brut). */
export const tokenFromQr = (raw) => {
  const s = (raw || "").trim();
  const m = s.match(/\/carte\/([A-Za-z0-9_-]{16,})/);
  if (m) return m[1];
  const last = s.split(/[/?#]/).filter(Boolean).pop() || "";
  return /^[A-Za-z0-9_-]{16,}$/.test(last) ? last : null;
};

/** Scanner caméra (smartphone/tablette/webcam) : lit le QR personnel du client et renvoie son jeton. */
export function CameraScanner({ onToken, onClose, parse = tokenFromQr, hint = "Visez le QR code de la carte du client", notFound = "QR code lu, mais ce n'est pas une carte Respondia" }) {
  const video = useRef(null); const canvas = useRef(null); const raf = useRef(null); const stream = useRef(null);
  const [error, setError] = useState(null); const [status, setStatus] = useState("Démarrage de la caméra…");
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        stream.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
        if (!alive) return;
        video.current.srcObject = stream.current; video.current.setAttribute("playsinline", "true"); await video.current.play();
        setStatus(hint);
        const tick = () => {
          if (!alive || !video.current) return;
          const v = video.current;
          if (v.readyState === v.HAVE_ENOUGH_DATA) {
            const c = canvas.current; c.width = v.videoWidth; c.height = v.videoHeight;
            const ctx = c.getContext("2d", { willReadFrequently: true }); ctx.drawImage(v, 0, 0, c.width, c.height);
            const img = ctx.getImageData(0, 0, c.width, c.height);
            const code = jsQR(img.data, img.width, img.height, { inversionAttempts: "dontInvert" });
            if (code?.data) { const t = parse(code.data); if (t) { alive = false; onToken(t); return; } setStatus(notFound); }
          }
          raf.current = requestAnimationFrame(tick);
        };
        raf.current = requestAnimationFrame(tick);
      } catch (e) { setError(e?.name === "NotAllowedError" ? "Accès à la caméra refusé. Autorisez la caméra dans les réglages du navigateur, ou saisissez le code manuellement." : "Caméra indisponible sur cet appareil. Saisissez le code manuellement."); }
    })();
    return () => { alive = false; if (raf.current) cancelAnimationFrame(raf.current); stream.current?.getTracks().forEach((t) => t.stop()); };
  }, [onToken, parse, hint, notFound]);
  return <div className="rounded-2xl overflow-hidden bg-black relative" data-testid="loyalty-camera">
    <video ref={video} muted className="w-full max-h-[60vh] object-cover" />
    <canvas ref={canvas} className="hidden" />
    <div className="absolute inset-0 pointer-events-none flex items-center justify-center"><div className="w-56 h-56 border-2 border-white/80 rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" /></div>
    <div className="absolute top-2 right-2"><Button size="sm" variant="secondary" onClick={onClose} className="rounded-full h-8 w-8 p-0" data-testid="loyalty-camera-close"><X className="w-4 h-4" /></Button></div>
    <div className="absolute bottom-0 inset-x-0 p-3 text-center text-xs text-white bg-gradient-to-t from-black/70" data-testid="loyalty-camera-status">{error || status}</div>
    {error && <div className="absolute inset-0 flex items-center justify-center"><Camera className="w-10 h-10 text-white/60" /></div>}
  </div>;
}
